import React from 'react';

const OfficialBillPrint = ({ student, payment, items = [], paymentMethod = "Cash", onPaymentMethodChange, printedBy = "Admin", isPrintView, onItemChange, onAddItem, onRemoveItem }) => {
  if (!student || !payment) return null;

  const validItems = isPrintView 
    ? items.filter(item => item.description && item.description.trim() !== '')
    : items;
    
  const displayItems = validItems.length > 0 
    ? validItems 
    : [{ description: `Consultancy & Processing Fee (${student.visa_type} Visa - ${student.abroad_study_country || 'Various'})`, amount: payment.paid_amount }];


  const totalAmount = parseFloat(payment.total_amount);
  const paidAmount = parseFloat(payment.paid_amount) || 0;
  const balanceDue = totalAmount - paidAmount;

  return (
    <div className="bill-container bg-white text-black leading-tight mx-auto relative print:m-0 print:p-0">
      <style>{`
        @media screen {
          .bill-container {
            box-shadow: 0 0 10px rgba(0,0,0,0.1);
            margin: 1rem auto;
            width: 210mm;
            height: 98mm;
            padding: 5mm;
          }
        }
        @media print {
          body {
            margin: 0;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
            background: white !important;
            font-family: "Times New Roman", Times, serif !important;
          }
          .no-print {
            display: none !important;
          }
          .bill-container {
            width: 100%;
            height: 98mm;
            padding: 5mm;
            margin: 0;
            box-shadow: none;
            display: block !important;
            page-break-inside: avoid;
          }
          @page {
            size: A4 portrait;
            margin: 0;
          }
        }
        .bill-container {
          font-family: "Times New Roman", Times, serif !important;
        }
        .balance-summary-container {
          display: flex;
          justify-content: flex-start;
          align-items: flex-end;
          gap: 40px;
          margin-top: 15px;
        }
        .balance-grid {
          display: flex;
          border: 1px solid black;
          width: fit-content;
        }
        .balance-box {
          border-right: 1px solid black;
          min-width: 100px;
        }
        .balance-box:last-child {
          border-right: none;
        }
        .balance-header {
          background: #222;
          color: white;
          text-align: center;
          padding: 3px 8px;
          font-weight: bold;
          font-size: 11px;
          border-bottom: 1px solid black;
          text-transform: uppercase;
        }
        .balance-value {
          text-align: center;
          padding: 8px 12px;
          font-size: 14px;
          font-weight: bold;
          min-height: 35px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .paid-by-section {
          flex: 1;
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          margin-bottom: 5px;
        }
        .paid-by-line {
          width: 180px;
          border-bottom: 1px solid black;
          margin-bottom: 2px;
        }
        .paid-by-label {
          font-weight: bold;
          font-size: 14px;
        }
        .payment-method-container {
          flex: 1;
          display: flex;
          flex-direction: column;
          gap: 5px;
          padding-left: 10px;
        }
        .payment-method-label {
          font-weight: bold;
          font-size: 12px;
          margin-bottom: 2px;
          text-decoration: underline;
        }
        .method-item {
          display: flex;
          items: center;
          gap: 10px;
          cursor: pointer;
        }
        .method-checkbox {
          width: 16px;
          height: 16px;
          border: 1px solid black;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 12px;
          font-weight: bold;
          flex-shrink: 0;
        }
        .method-text {
          font-size: 13px;
          font-weight: 500;
        }
      `}</style>

      {/* Header Section */}
      <div className="flex justify-between items-start mb-3 border-b border-black pb-3">
        <div className="w-20">
          <img src={process.env.PUBLIC_URL + '/logo.png'} alt="CSK Logo" className="w-full object-contain" />
        </div>
        
        <div className="text-center flex-1 px-4">
          <h1 className="text-xl font-bold tracking-tight uppercase leading-none">C.S.K Language & Consultancy P. Ltd.</h1>
          <p className="text-sm">Kalanki-14, Kathmandu, Nepal</p>
          <p className="text-sm">Phone: +977-1-5233123 | Email: info@csk.edu.np</p>
          <div className="mt-2">
            <h2 className="text-sm font-bold border-b border-black inline-block px-6 py-0.5 uppercase tracking-widest bg-gray-100">Official Invoice</h2>
          </div>
        </div>

        <div className="w-32 text-right pt-1">
          <p className="text-xs font-bold whitespace-nowrap">Regd. No.: 48708/064/65</p>
        </div>
      </div>

      <div className="flex justify-between mb-3 text-sm">
        <div>
          <p className="mb-0.5"><span className="font-bold w-20 inline-block">Bill To:</span> <span className="font-bold uppercase">{student.full_name}</span></p>
          <p className="mb-0.5"><span className="font-bold w-20 inline-block">Visa Type:</span> {student.visa_type} Visa</p>
          <p className="mb-0.5"><span className="font-bold w-20 inline-block">Status:</span> <span className="uppercase font-bold border px-1 border-black">{payment.status}</span></p>
        </div>
        <div className="text-right">
          <p className="mb-0.5"><span className="font-bold">Invoice No:</span> #{payment.id}</p>
          <p className="mb-0.5"><span className="font-bold">Date:</span> {new Date(payment.payment_date || payment.created_at).toLocaleDateString('en-GB')}</p>
          <p className="mt-2 text-[10px] font-bold text-black uppercase tracking-tighter border-t border-gray-100 pt-0.5 whitespace-nowrap">
            Printed By: {printedBy || "Authorized Staff"} | {new Date().toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
          </p>
        </div>
      </div>

      {/* Bill Details Table */}
      <table className="w-full mb-3 border-collapse border border-black text-xs">
        <thead>
          <tr className="bg-gray-100">
            <th className="border border-black p-1.5 text-center w-10">S.N.</th>
            <th className="border border-black p-1.5 text-left">Description</th>
            <th className="border border-black p-1.5 text-right w-32">Paid Amount (NRs)</th>
          </tr>
        </thead>
        <tbody>
          {displayItems.map((item, index) => (
            <tr key={index} className="group">
              <td className="border border-black p-1.5 text-center">{index + 1}</td>
              <td className="border border-black p-1.5 font-semibold relative">
                {isPrintView ? (
                  <span>{item.description}</span>
                ) : (
                  <div className="flex items-center">
                    <input 
                      type="text" 
                      value={item.description} 
                      onChange={(e) => onItemChange && onItemChange(index, 'description', e.target.value)}
                      className="w-full bg-transparent border-none outline-none focus:ring-2 focus:ring-indigo-500 rounded"
                      placeholder="Enter description..."
                    />
                    {items.length > 1 && (
                      <button onClick={() => onRemoveItem && onRemoveItem(index)} className="absolute right-1 text-red-500 opacity-0 group-hover:opacity-100 no-print transition-opacity" title="Remove row">
                         &times;
                      </button>
                    )}
                  </div>
                )}
              </td>
              <td className="border border-black p-1.5 text-right font-semibold relative">
                {isPrintView ? (
                  <span>{parseFloat(item.amount || 0).toLocaleString('en-IN', {minimumFractionDigits: 2})}</span>
                ) : (
                  <input 
                    type="number" 
                    value={item.amount} 
                    onChange={(e) => onItemChange && onItemChange(index, 'amount', e.target.value)}
                    className="w-full bg-transparent border-none outline-none focus:ring-2 focus:ring-indigo-500 rounded px-1 -mx-1 text-right"
                    placeholder="0.00"
                  />
                )}
              </td>
            </tr>
          ))}
          {!isPrintView && (
            <tr className="no-print">
              <td colSpan="3" className="border border-black p-1.5 text-center bg-gray-50 hover:bg-gray-100 cursor-pointer transition-colors" onClick={onAddItem}>
                 <span className="text-emerald-600 font-bold text-xs">+ Add Row</span>
              </td>
            </tr>
          )}
        </tbody>
      </table>

      {/* New Balance Grid and Paid By Section */}
      <div className="balance-summary-container">
        <div className="balance-grid">
          <div className="balance-box">
            <div className="balance-header">Pre Balance</div>
            <div className="balance-value">{totalAmount.toLocaleString('en-IN', {minimumFractionDigits: 2})}</div>
          </div>
          <div className="balance-box">
            <div className="balance-header">Amount Paid</div>
            <div className="balance-value">{paidAmount.toLocaleString('en-IN', {minimumFractionDigits: 2})}</div>
          </div>
          <div className="balance-box">
            <div className="balance-header">Due Balance</div>
            <div className="balance-value">{balanceDue.toLocaleString('en-IN', {minimumFractionDigits: 2})}</div>
          </div>
        </div>

        <div className="payment-method-container">
          <div className="payment-method-label">Payment Method:</div>
          <div className="flex gap-6">
            {['Cash', 'Cheque', 'QR'].map(method => (
              <div 
                key={method} 
                className="method-item"
                onClick={() => !isPrintView && onPaymentMethodChange && onPaymentMethodChange(method)}
              >
                <div className="method-checkbox">
                  {paymentMethod === method ? '✓' : ''}
                </div>
                <span className="method-text">{method}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Signatures */}
      <div className="mt-8 flex justify-between h-10 items-end px-6">
        <div className="w-36 text-center">
          <div className="border-t border-black mb-1"></div>
          <p className="font-bold text-xs">Receiver's Signature</p>
        </div>
        <div className="w-36 text-center">
          <div className="border-t border-black mb-1"></div>
          <p className="font-bold text-xs">Authorized Signature</p>
        </div>
      </div>

      <div className="mt-6 pt-2 border-t border-gray-200 text-[9px] flex justify-between items-center text-gray-500 px-2">
        <div className="flex gap-4">
          <span>Thank you for choosing C.S.K Language & Consultancy P. Ltd.</span>
        </div>
        <div className="flex items-center gap-1.5 font-bold text-black">
          <span>Bill Created By:</span>
          <span className="uppercase">{payment.creator_name || "System"}</span>
          <span className="text-gray-300">|</span>
          <span>{new Date(payment.created_at).toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
        </div>
      </div>
    </div>
  );
};

export default OfficialBillPrint;
